import { useEffect } from "react";
import { clone } from "./studioModel";
import { portableSelection } from "./studioGroups";

// Studio keyboard shortcuts (see ShortcutsDialog). Typing in fields, open
// dialogs and busy history steps are left alone.
export default function useStudioShortcuts(context) {
  const { view, preview, setPreview, doc, focusedLayerId, dialog, activeStyleGroup, setSelected, page, nodes, duplicate, group, historyBusy,
    ungroup, paste, patch, redo, remove, save, undo, notice, space, clipboard, setTool } = context;
  useEffect(() => {
    const keydown = (e) => {
      if (view === "cms") return;
      if (
        e.target.closest('input,textarea,select,[contenteditable="true"]') ||
        dialog || historyBusy
      )
        return;
      if (!page) return;
      if (preview) { if (e.key === "Escape") setPreview(false); return; }
      const mod = e.metaKey || e.ctrlKey,
        key = e.key.toLowerCase();
      if (key === " ") {
        e.preventDefault();
        space.current = true;
      }
      if (
        activeStyleGroup &&
        ((mod && ["d", "c", "v", "g"].includes(key)) ||
          key === "delete" ||
          key === "backspace")
      ) {
        e.preventDefault();
        return;
      }
      if (mod && ["z", "y", "a", "d", "c", "v", "g", "s"].includes(key)) {
        e.preventDefault();
        if (key === "z") e.shiftKey ? redo() : undo();
        if (key === "y") redo();
        if (key === "a")
          setSelected(
            page.elements.filter((n) => !n.locked && !n.hidden).map((n) => n.id)
          );
        if (key === "d") duplicate();
        if (key === "c") {
          clipboard.current = clone(portableSelection(doc.pages.flatMap((p) => p.elements), nodes.map((n) => n.id), focusedLayerId));
          notice(`${nodes.length} elements copied`);
        }
        if (key === "v") paste(clipboard.current);
        if (key === "g") e.shiftKey ? ungroup() : group();
        if (key === "s") save();
        return;
      }
      if (mod || e.altKey) return;
      if (key === "v") setTool("select");
      if (key === "h") setTool("hand");
      if (key === "escape") {
        e.preventDefault();
        setSelected([]);
      }
      if (key === "delete" || key === "backspace") {
        e.preventDefault();
        remove();
      }
      if (key.startsWith("arrow") && nodes.length) {
        e.preventDefault();
        const step = e.shiftKey ? 10 : 1;
        patch((n) => ({
          ...n,
          x:
            n.x +
            (key === "arrowleft" ? -step : key === "arrowright" ? step : 0),
          y: n.y + (key === "arrowup" ? -step : key === "arrowdown" ? step : 0),
        }));
      }
    };
    const keyup = (e) => {
      if (e.code === "Space") space.current = false;
    };
    const blur = () => {
      space.current = false;
    };
    window.addEventListener("keydown", keydown);
    window.addEventListener("keyup", keyup);
    window.addEventListener("blur", blur);
    return () => {
      window.removeEventListener("keydown", keydown);
      window.removeEventListener("keyup", keyup);
      window.removeEventListener("blur", blur);
    };
  }, [
    view,
    preview,
    doc?.pages,
    focusedLayerId,
    dialog,
    activeStyleGroup,
    setSelected,
    page,
    nodes,
    duplicate,
    group,
    historyBusy,
    ungroup,
    paste,
    patch,
    redo,
    remove,
    save,
    undo,
    notice,
    clipboard,
    setPreview,
    setTool,
    space,
  ]);
}
